// Premium, branded, printable patient report (on-device HTML → print → PDF).
// CADtomie identity: compass glyph, accent gradient, inline score ring + curve
// sparklines. Light theme for paper. Beats OPED on content and design while
// staying honest (estimated ROM labeled, redundancy removed).

import type { AnalysisResult, GaitView, Milestone, Patient, Session } from "../store/types";
import type { Scores } from "../gait/scoring";
import type { ClinicalMetrics, SidePair } from "../gait/clinical";
import type { SagittalRom, JointKey } from "../gait/rom";
import { referenceRange, evaluateNorm, type NormSeverity } from "../gait/norms";
import { REFERENCE_CURVES, JOINT_LABEL } from "../gait/refCurves";
import { interpret } from "../gait/interpret";

const SEV: Record<NormSeverity, string> = {
  NORMAL: "#15803d",
  MILD: "#65a30d",
  MODERATE: "#b45309",
  SEVERE: "#b91c1c",
};

function fmt(n: number | null | undefined, d = 1, unit = ""): string {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  return `${n.toFixed(d)}${unit ? " " + unit : ""}`;
}
function pair(p: SidePair, d = 0, unit = ""): string {
  return `${fmt(p.L, d)} / ${fmt(p.R, d)}${unit ? " " + unit : ""}`;
}
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string);
}
function scoreColor(v: number): string {
  if (v >= 80) return "#0d9488";
  if (v >= 60) return "#0a84ff";
  if (v >= 40) return "#d97706";
  return "#dc2626";
}

/* ── Inline SVG: score ring ── */
function ringSvg(value: number, size = 120): string {
  const stroke = 13;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - value / 100);
  const col = scoreColor(value);
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="#e2e8f0" stroke-width="${stroke}"/>
    <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="${col}" stroke-width="${stroke}"
      stroke-linecap="round" stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}"
      transform="rotate(-90 ${size / 2} ${size / 2})"/>
    <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="${size * 0.3}" font-weight="800" fill="#0f172a">${value}</text>
  </svg>`;
}

/* ── Inline SVG: mini kinematic curve with reference band ── */
function miniCurve(joint: JointKey, rom: SagittalRom): string {
  const W = 230, H = 80, P = 6;
  const ref = REFERENCE_CURVES[joint];
  const L = rom.curves[joint].L;
  const R = rom.curves[joint].R;
  const all = [...ref.low, ...ref.high, ...L, ...R].filter((v) => isFinite(v));
  const lo = Math.min(...all), hi = Math.max(...all);
  const x = (i: number) => P + (i / 100) * (W - 2 * P);
  const y = (v: number) => P + (1 - (v - lo) / (hi - lo || 1)) * (H - 2 * P);
  const path = (s: number[]) => s.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const band =
    ref.high.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("") +
    [...ref.low].reverse().map((v, i) => `L${x(100 - i).toFixed(1)},${y(v).toFixed(1)}`).join("") + "Z";
  const toX = x(62);
  return `<div style="text-align:center">
    <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
      <path d="${band}" fill="#e8eef6"/>
      <line x1="${toX}" y1="${P}" x2="${toX}" y2="${H - P}" stroke="#f59e0b" stroke-width="1" stroke-dasharray="3 2"/>
      <path d="${path(ref.mean)}" fill="none" stroke="#94a3b8" stroke-width="1" stroke-dasharray="4 3"/>
      <path d="${path(L)}" fill="none" stroke="#0a84ff" stroke-width="2"/>
      <path d="${path(R)}" fill="none" stroke="#0d9488" stroke-width="2"/>
    </svg>
    <div style="font-size:10px;color:#475569">${esc(JOINT_LABEL[joint])}</div>
  </div>`;
}

interface Row { label: string; value: string; reference: string; status: string; color: string; }

function normRow(label: string, value: number | null, key: string, heightCm: number | undefined, d = 1): Row {
  const range = referenceRange(key, { heightCm });
  if (value === null || value === undefined || !range)
    return { label, value: fmt(value, d), reference: range ? `${fmt(range.low, 0)}–${fmt(range.high, 0)} ${range.unit}` : "—", status: "—", color: "#64748b" };
  const ev = evaluateNorm(value, range);
  return {
    label,
    value: `${fmt(value, d)} ${range.unit}`,
    reference: `${fmt(range.low, 0)}–${fmt(range.high, 0)} ${range.unit}`,
    status: ev.severity === "NORMAL" ? "in range" : `${ev.position} · ${ev.severity.toLowerCase()}`,
    color: SEV[ev.severity],
  };
}
function plain(label: string, value: string): Row {
  return { label, value, reference: "—", status: "—", color: "#0f172a" };
}

function clinicalRows(c: ClinicalMetrics, heightCm?: number): Row[] {
  const rows: Row[] = [plain("Steps", String(c.stepCount)), normRow("Cadence", c.cadenceStepsPerMin, "cadenceStepsPerMin", heightCm, 0)];
  if (c.speedKmh !== null) rows.push(normRow("Speed (est.)", c.speedKmh, "speedKmh", heightCm, 1));
  rows.push(plain("Stance time L/R", pair(c.stanceTimePct, 0, "%")));
  rows.push(plain("Double support L/R", pair(c.doubleSupportPct, 0, "%")));
  rows.push(plain("Step time L/R", pair(c.stepTimeS, 2, "s")));
  if (c.stepLengthCm.L !== null || c.stepLengthCm.R !== null)
    rows.push(plain("Step length L/R (est.)", pair(c.stepLengthCm, 0, "cm")));
  rows.push(plain("Gait variability L/R", pair(c.gaitVariabilityPct, 0, "%")));
  return rows;
}

function glyphSvg(): string {
  return `<svg width="26" height="26" viewBox="0 0 26 26"><g stroke="#fff" stroke-width="3.4" stroke-linecap="round" fill="none">
    <line x1="7" y1="22" x2="13" y2="5"/><line x1="19" y1="22" x2="13" y2="5"/></g><circle cx="13" cy="5" r="3" fill="#fff"/></svg>`;
}

/* ── Camera perspective: near/far-leg reliability (light theme) ── */
const CONF_PILL: Record<string, { bg: string; fg: string; label: string }> = {
  high: { bg: "#dcfce7", fg: "#15803d", label: "High confidence" },
  medium: { bg: "#dbeafe", fg: "#1d4ed8", label: "Medium confidence" },
  low: { bg: "#fef3c7", fg: "#b45309", label: "Low confidence" },
};

function legFigureSvg(near: "L" | "R"): string {
  const hipX = 60, hipY = 92;
  const front = "60,92 78,126 92,162", frontFoot = ["92", "162", "108", "162"];
  const back = "60,92 44,128 30,164", backFoot = ["30", "164", "46", "164"];
  const nearPts = near === "L" ? front : back;
  const nearFoot = near === "L" ? frontFoot : backFoot;
  const farPts = near === "L" ? back : front;
  const farFoot = near === "L" ? backFoot : frontFoot;
  return `<svg viewBox="0 0 120 180" width="100" height="150">
    <defs><linearGradient id="legrep" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#0a84ff"/><stop offset="100%" stop-color="#0d9488"/></linearGradient></defs>
    <g stroke="#cbd5e1" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke-dasharray="2 7">
      <polyline points="${farPts}"/><line x1="${farFoot[0]}" y1="${farFoot[1]}" x2="${farFoot[2]}" y2="${farFoot[3]}"/></g>
    <g stroke="#94a3b8" stroke-width="7" stroke-linecap="round" fill="none"><line x1="${hipX}" y1="${hipY}" x2="66" y2="44"/></g>
    <circle cx="68" cy="32" r="11" fill="#94a3b8"/>
    <g stroke="url(#legrep)" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" fill="none">
      <polyline points="${nearPts}"/><line x1="${nearFoot[0]}" y1="${nearFoot[1]}" x2="${nearFoot[2]}" y2="${nearFoot[3]}"/></g>
  </svg>`;
}

function legConfidenceSection(analysis: AnalysisResult): string {
  const lp = analysis.legProximity;
  if (!lp) return "";
  const confOf = (side: "L" | "R"): string => {
    const f = (analysis.findings ?? []).find((x) => x.side === side);
    if (f) return f.confidence;
    const v = side === lp.near ? lp.nearVisibility : lp.farVisibility;
    return v >= 0.7 ? "high" : v >= 0.55 ? "medium" : "low";
  };
  const meter = (role: string, side: "L" | "R", vis: number, conf: string, near: boolean): string => {
    const pct = Math.round(vis * 100);
    const p = CONF_PILL[conf] ?? CONF_PILL.low;
    const fill = near ? "linear-gradient(90deg,#0a84ff,#0d9488)" : "#cbd5e1";
    return `<div class="lmrow">
      <div class="lmhead"><span><b>${role}</b> · ${side}</span><span class="cpill" style="background:${p.bg};color:${p.fg}">${p.label}</span></div>
      <div class="lmbar"><div style="width:${pct}%;background:${fill}"></div></div>
      <div class="muted" style="margin-top:3px">${pct}% visible</div>
    </div>`;
  };
  const nearConf = confOf(lp.near);
  const farConf = confOf(lp.far);
  const clear = lp.separation >= 0.12;
  const note = clear
    ? `The ${lp.near} leg is camera-side and most reliable. The ${lp.far} leg is partially occluded by the body, so its metrics are read with ${(CONF_PILL[farConf] ?? CONF_PILL.low).label.toLowerCase()}.`
    : `Both legs are similarly visible — neither side is favoured for this clip.`;
  return `<h2>Camera perspective</h2>
    <div class="legwrap">
      <div class="legfig">${legFigureSvg(lp.near)}</div>
      <div class="legmeters">
        ${meter("Camera-side", lp.near, lp.nearVisibility, nearConf, true)}
        ${meter("Far (occluded)", lp.far, lp.farVisibility, farConf, false)}
      </div>
    </div>
    <p class="muted" style="font-size:11px;margin-top:8px">${esc(note)}</p>`;
}

export function buildReportHtml(session: Session, patient: Patient | null, analysis: AnalysisResult): string {
  const s = analysis.scores;
  const interp = s
    ? interpret({ view: session.view, scores: s, findings: analysis.findings ?? [], temporal: analysis.temporal, clinical: analysis.clinical, heightCm: patient?.heightCm })
    : null;
  const rows = analysis.clinical ? clinicalRows(analysis.clinical, patient?.heightCm) : [];

  const subBar = (label: string, v?: number) =>
    v === undefined
      ? `<div class="sb"><span>${label}</span><b>n/a</b></div>`
      : `<div class="sb"><span>${label}</span><div class="track"><div style="width:${v}%;background:${scoreColor(v)}"></div></div><b>${v}</b></div>`;

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>CADtomie Gait — ${esc(patient?.code ?? "Report")}</title>
<style>
  *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;margin:0}
  .wrap{max-width:820px;margin:0 auto;padding:0 28px 28px}
  .hdr{background:linear-gradient(120deg,#0a84ff,#0d9488);color:#fff;padding:20px 28px;display:flex;align-items:center;gap:12px;margin:0 -28px 18px;border-radius:0 0 18px 18px}
  .hdr h1{font-size:20px;margin:0;font-weight:800;letter-spacing:-.02em}
  .hdr .sub{font-size:11px;opacity:.85}
  .hdr .right{margin-left:auto;text-align:right;font-size:11px;opacity:.9}
  h2{font-size:13px;text-transform:uppercase;letter-spacing:.05em;color:#0d9488;margin:20px 0 8px}
  .demo{font-size:12px;color:#334155;line-height:1.7;display:flex;gap:24px;flex-wrap:wrap}
  .scorebox{display:flex;gap:22px;align-items:center}
  .subs{flex:1;display:flex;flex-direction:column;gap:7px}
  .sb{display:flex;align-items:center;gap:10px;font-size:12px}
  .sb span{width:120px;color:#475569}.sb b{width:26px;text-align:right}
  .track{flex:1;height:7px;background:#e2e8f0;border-radius:99px;overflow:hidden}.track>div{height:100%;border-radius:99px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0}
  th{background:#f1f5f9;color:#0f172a;font-size:11px;text-transform:uppercase;letter-spacing:.03em}
  ul{margin:6px 0;padding-left:18px;font-size:12px}.flag{color:#b91c1c}
  .muted{color:#64748b;font-size:10px}.est{color:#b45309;font-size:10px}
  .curves{display:flex;gap:8px;flex-wrap:wrap;justify-content:space-between}
  .legwrap{display:flex;gap:18px;align-items:center;margin-top:6px}
  .legfig{flex:0 0 auto;width:118px;height:152px;border:1px solid #e2e8f0;border-radius:12px;display:flex;align-items:center;justify-content:center;background:#f8fafc}
  .legmeters{flex:1;display:flex;flex-direction:column;gap:13px}
  .lmrow{font-size:12px}
  .lmhead{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:5px}
  .lmbar{height:8px;background:#e2e8f0;border-radius:99px;overflow:hidden}.lmbar>div{height:100%;border-radius:99px}
  .cpill{font-size:9.5px;font-weight:700;padding:2px 8px;border-radius:99px;white-space:nowrap}
  .notes{height:54px;border:1px solid #e2e8f0;border-radius:8px;margin-top:4px}
  @media print{.hdr{-webkit-print-color-adjust:exact;print-color-adjust:exact}h2{page-break-after:avoid}}
</style></head><body>
  <div class="hdr">
    ${glyphSvg()}
    <div><h1>CADtomie Gait</h1><div class="sub">${session.view === "frontal" ? "Frontal" : "Lateral"} gait screening · on-device</div></div>
    <div class="right">Patient <b>${esc(patient?.code ?? "—")}</b><br>${new Date(session.recordedAt).toLocaleDateString()} · ${session.milestone}</div>
  </div>
  <div class="wrap">
    <div class="demo">
      ${patient?.sex ? `<span>Sex: <b>${esc(patient.sex)}</b></span>` : ""}
      ${patient?.heightCm ? `<span>Height: <b>${patient.heightCm} cm</b></span>` : ""}
      ${patient?.birthYear ? `<span>Birth year: <b>${patient.birthYear}</b></span>` : ""}
      <span>Cycles: <b>${session.qc.detectedCycles}</b></span>
      ${s ? `<span>Confidence: <b>${s.confidence}</b></span>` : ""}
    </div>

    ${
      s
        ? `<h2>Overall Gait Score</h2>
    <div class="scorebox">${ringSvg(s.overall)}
      <div class="subs">
        ${subBar("Symmetry", s.symmetry)}${subBar("Pelvic stability", s.pelvicStability)}${subBar("Knee control", s.kneeControl)}${subBar("Trunk stability", s.trunkStability)}${subBar("Gait consistency", s.consistency)}
      </div></div>`
        : ""
    }

    ${legConfidenceSection(analysis)}

    ${
      interp
        ? `<h2>Interpretation</h2>
    <p style="font-size:12.5px">${esc(interp.summary)}</p>
    <p style="font-size:12.5px"><b>Asymmetry:</b> ${esc(interp.asymmetry)}</p>
    ${interp.keyFindings.length ? `<b style="font-size:12px">Key findings</b><ul>${interp.keyFindings.map((k) => `<li>${esc(k)}</li>`).join("")}</ul>` : ""}
    ${interp.flags.length ? `<b style="font-size:12px">Flags</b><ul>${interp.flags.map((f) => `<li class="flag">${esc(f)}</li>`).join("")}</ul>` : ""}`
        : ""
    }

    ${
      rows.length
        ? `<h2>Gait metrics</h2>
    <table><thead><tr><th>Metric</th><th>Value</th><th>Reference</th><th>Status</th></tr></thead>
    <tbody>${rows.map((r) => `<tr><td>${r.label}</td><td>${r.value}</td><td>${r.reference}</td><td style="color:${r.color}">${r.status}</td></tr>`).join("")}</tbody></table>`
        : ""
    }

    ${
      analysis.rom
        ? `<h2>Joint motion <span class="est">(estimated · ${analysis.rom.confidence} confidence · not for diagnosis)</span></h2>
    <div class="curves">${miniCurve("hip", analysis.rom)}${miniCurve("knee", analysis.rom)}${miniCurve("ankle", analysis.rom)}</div>
    <div style="font-size:10px;color:#94a3b8;margin-top:2px">Shaded band and dashed line are an illustrative comparison shape for movement-range context, not a clinical normative database.</div>
    <table style="margin-top:10px"><thead><tr><th>Joint</th><th>ROM L</th><th>Range L</th><th>ROM R</th><th>Range R</th></tr></thead><tbody>
      ${(["hip", "knee", "ankle"] as JointKey[]).map((k) => {
        const j = analysis.rom![k];
        return `<tr><td>${k[0].toUpperCase() + k.slice(1)}</td><td>${fmt(j.L.excursion, 0, "°")}</td><td>${fmt(j.L.min, 0)}…${fmt(j.L.max, 0)}°</td><td>${fmt(j.R.excursion, 0, "°")}</td><td>${fmt(j.R.min, 0)}…${fmt(j.R.max, 0)}°</td></tr>`;
      }).join("")}
    </tbody></table>`
        : ""
    }

    ${
      analysis.findings && analysis.findings.length
        ? `<h2>Categorical findings</h2>
    <table><thead><tr><th>Finding</th><th>Band</th><th>Confidence</th></tr></thead>
    <tbody>${analysis.findings.map((f) => `<tr><td>${esc(f.label)}</td><td>${f.band}</td><td>${f.confidence}</td></tr>`).join("")}</tbody></table>`
        : ""
    }

    <h2>Clinician notes</h2>
    <div class="notes"></div>
    <p class="muted">Signature: ____________________________</p>
    <p class="muted">${interp ? esc(interp.disclaimer) : ""}<br>
    Versions — pose ${session.versions.poseModel}, features ${session.versions.feature}, thresholds ${session.versions.threshold}, scoring ${session.versions.scoring}.
    Phase model after Perry / Götz-Neumann. Generated on-device by CADtomie Gait — no data left this device.</p>
  </div>
</body></html>`;
}

/* ════════════════════════════════════════════════════════════════════════
   Follow-up / progress report (longitudinal). Presents already-computed
   per-session scores over time — no new measurement or modelling. Light
   theme, same identity as the single-session report. On-device HTML → print.
   ════════════════════════════════════════════════════════════════════════ */

export interface TrendPoint {
  t: number; // recordedAt
  view: GaitView;
  milestone: Milestone;
  overall: number;
  cadence: number | null;
  confidence: string;
  scores?: Scores;
}

const MILESTONE_LABEL: Record<Milestone, string> = {
  baseline: "Baseline",
  "6w": "6 weeks",
  "3m": "3 months",
  "6m": "6 months",
  "12m": "12 months",
  adhoc: "Ad-hoc",
};

const VIEW_COLOR: Record<GaitView, string> = { lateral: "#0a84ff", frontal: "#0d9488" };

/* ── Inline SVG: overall-score trend, one line per view ── */
function trendChartSvg(points: TrendPoint[]): string {
  const W = 760, H = 210, PL = 34, PR = 14, PT = 14, PB = 26;
  const ts = points.map((p) => p.t);
  const minT = Math.min(...ts), maxT = Math.max(...ts);
  const spanT = maxT - minT || 1;
  const x = (t: number) => PL + ((t - minT) / spanT) * (W - PL - PR);
  const y = (v: number) => PT + (1 - v / 100) * (H - PT - PB);
  let grid = "";
  for (const g of [0, 25, 50, 75, 100]) {
    const yy = y(g);
    grid += `<line x1="${PL}" y1="${yy.toFixed(1)}" x2="${W - PR}" y2="${yy.toFixed(1)}" stroke="#e2e8f0" stroke-width="1"/>`;
    grid += `<text x="${PL - 6}" y="${(yy + 3).toFixed(1)}" text-anchor="end" font-size="9" fill="#94a3b8">${g}</text>`;
  }
  let series = "";
  (["lateral", "frontal"] as GaitView[]).forEach((view) => {
    const ps = points.filter((p) => p.view === view).sort((a, b) => a.t - b.t);
    if (!ps.length) return;
    const col = VIEW_COLOR[view];
    if (ps.length > 1) {
      const d = ps.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.overall).toFixed(1)}`).join("");
      series += `<path d="${d}" fill="none" stroke="${col}" stroke-width="2.5" stroke-linejoin="round"/>`;
    }
    series += ps.map((p) => `<circle cx="${x(p.t).toFixed(1)}" cy="${y(p.overall).toFixed(1)}" r="3.6" fill="${col}"/>`).join("");
  });
  const d = (t: number) => new Date(t).toLocaleDateString();
  const xlabels =
    `<text x="${PL}" y="${H - 8}" font-size="9" fill="#64748b">${d(minT)}</text>` +
    (maxT !== minT ? `<text x="${W - PR}" y="${H - 8}" text-anchor="end" font-size="9" fill="#64748b">${d(maxT)}</text>` : "");
  return `<svg width="100%" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">${grid}${series}${xlabels}</svg>`;
}

export function buildTrendReportHtml(patient: Patient | null, pointsIn: TrendPoint[]): string {
  const points = [...pointsIn].sort((a, b) => a.t - b.t);
  const latest = points[points.length - 1] ?? null;
  const first = points[0] ?? null;
  const spanDays = first && latest ? Math.round((latest.t - first.t) / 86400000) : 0;
  const sameViewFirst = latest ? points.find((p) => p.view === latest.view) : undefined;
  const delta = latest && sameViewFirst && sameViewFirst !== latest ? latest.overall - sameViewFirst.overall : null;
  const latestScores = [...points].reverse().find((p) => p.scores)?.scores;

  const subBar = (label: string, v?: number) =>
    v === undefined
      ? ""
      : `<div class="sb"><span>${label}</span><div class="track"><div style="width:${v}%;background:${scoreColor(v)}"></div></div><b>${v}</b></div>`;

  const rows = points
    .map((p, i, arr) => {
      const prev = arr.slice(0, i).reverse().find((x) => x.view === p.view);
      const dv = prev ? p.overall - prev.overall : null;
      const dtxt = dv === null ? "—" : dv > 0 ? `+${dv}` : `${dv}`;
      const dcol = dv === null ? "#64748b" : dv > 0 ? "#15803d" : dv < 0 ? "#b91c1c" : "#64748b";
      return `<tr><td>${new Date(p.t).toLocaleDateString()}</td><td>${MILESTONE_LABEL[p.milestone]}</td><td>${p.view === "frontal" ? "Frontal" : "Lateral"}</td><td><b>${p.overall}</b></td><td style="color:${dcol}">${dtxt}</td><td>${fmt(p.cadence, 0)}</td><td>${p.confidence}</td></tr>`;
    })
    .join("");

  const deltaPill =
    delta === null
      ? ""
      : `<span style="font-weight:700;color:${delta > 0 ? "#15803d" : delta < 0 ? "#b91c1c" : "#64748b"}">${delta > 0 ? "▲ +" : delta < 0 ? "▼ " : "± "}${delta} since baseline (${latest!.view === "frontal" ? "frontal" : "lateral"})</span>`;

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>CADtomie Gait — Progress · ${esc(patient?.code ?? "Report")}</title>
<style>
  *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;margin:0}
  .wrap{max-width:820px;margin:0 auto;padding:0 28px 28px}
  .hdr{background:linear-gradient(120deg,#0a84ff,#0d9488);color:#fff;padding:20px 28px;display:flex;align-items:center;gap:12px;margin:0 -28px 18px;border-radius:0 0 18px 18px}
  .hdr h1{font-size:20px;margin:0;font-weight:800;letter-spacing:-.02em}
  .hdr .sub{font-size:11px;opacity:.85}
  .hdr .right{margin-left:auto;text-align:right;font-size:11px;opacity:.9}
  h2{font-size:13px;text-transform:uppercase;letter-spacing:.05em;color:#0d9488;margin:20px 0 8px}
  .demo{font-size:12px;color:#334155;line-height:1.7;display:flex;gap:24px;flex-wrap:wrap}
  .scorebox{display:flex;gap:22px;align-items:center}
  .subs{flex:1;display:flex;flex-direction:column;gap:7px}
  .sb{display:flex;align-items:center;gap:10px;font-size:12px}
  .sb span{width:120px;color:#475569}.sb b{width:26px;text-align:right}
  .track{flex:1;height:7px;background:#e2e8f0;border-radius:99px;overflow:hidden}.track>div{height:100%;border-radius:99px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0}
  th{background:#f1f5f9;color:#0f172a;font-size:11px;text-transform:uppercase;letter-spacing:.03em}
  .muted{color:#64748b;font-size:10px}
  .legend{display:flex;gap:16px;font-size:11px;color:#475569;margin-top:6px}
  .legend i{display:inline-block;width:18px;height:3px;border-radius:2px;vertical-align:middle;margin-right:5px}
  @media print{.hdr{-webkit-print-color-adjust:exact;print-color-adjust:exact}h2{page-break-after:avoid}}
</style></head><body>
  <div class="hdr">
    ${glyphSvg()}
    <div><h1>CADtomie Gait — Progress</h1><div class="sub">Longitudinal follow-up · on-device</div></div>
    <div class="right">Patient <b>${esc(patient?.code ?? "—")}</b><br>${points.length} measurement${points.length === 1 ? "" : "s"}${spanDays > 0 ? ` · ${spanDays} days` : ""}</div>
  </div>
  <div class="wrap">
    <div class="demo">
      ${patient?.sex ? `<span>Sex: <b>${esc(patient.sex)}</b></span>` : ""}
      ${patient?.heightCm ? `<span>Height: <b>${patient.heightCm} cm</b></span>` : ""}
      ${patient?.birthYear ? `<span>Birth year: <b>${patient.birthYear}</b></span>` : ""}
      ${first ? `<span>From: <b>${new Date(first.t).toLocaleDateString()}</b></span>` : ""}
      ${latest ? `<span>To: <b>${new Date(latest.t).toLocaleDateString()}</b></span>` : ""}
    </div>

    ${
      latest
        ? `<h2>Overall Gait Score — latest</h2>
    <div class="scorebox">${ringSvg(latest.overall)}
      <div class="subs">
        ${deltaPill ? `<div style="font-size:12.5px;margin-bottom:4px">${deltaPill}</div>` : ""}
        ${
          latestScores
            ? `${subBar("Symmetry", latestScores.symmetry)}${subBar("Pelvic stability", latestScores.pelvicStability)}${subBar("Knee control", latestScores.kneeControl)}${subBar("Trunk stability", latestScores.trunkStability)}${subBar("Gait consistency", latestScores.consistency)}`
            : `<div class="muted">Sub-scores unavailable for the latest session.</div>`
        }
      </div></div>`
        : `<p class="muted">No analysed measurements yet.</p>`
    }

    ${
      points.length > 1
        ? `<h2>Score over time</h2>
    ${trendChartSvg(points)}
    <div class="legend"><span><i style="background:${VIEW_COLOR.lateral}"></i>Lateral</span><span><i style="background:${VIEW_COLOR.frontal}"></i>Frontal</span></div>`
        : ""
    }

    ${
      rows
        ? `<h2>Measurements</h2>
    <table><thead><tr><th>Date</th><th>Milestone</th><th>View</th><th>Score</th><th>Δ vs prev (same view)</th><th>Cadence</th><th>Confidence</th></tr></thead>
    <tbody>${rows}</tbody></table>`
        : ""
    }

    <p class="muted" style="margin-top:16px">Progress is only comparable across compatible algorithm versions.
    Functional gait screening — not a diagnostic device, no clinical joint angles. Generated on-device by CADtomie Gait — no data left this device.</p>
  </div>
</body></html>`;
}

/* ════════════════════════════════════════════════════════════════════════
   Flex (fitness/wellness) screening report. Deliberately separate from the
   clinical gait report above — its own header, its own (non-clinical)
   vocabulary, and a mandatory wellness disclaimer in the footer. Never
   renders a raw degree/magnitude value, only categorical bands and the
   Beweglichkeitsalter composite. See docs/FLEX_WELLNESS_ABGRENZUNG.md.
   ════════════════════════════════════════════════════════════════════════ */

import type { FlexBand, FlexFinding, FlexRegion, FlexResult } from "../flex/types";
import { FLEX_REGION_LABEL } from "../flex/types";

const FLEX_BAND_COLOR: Record<FlexBand, string> = {
  GUT: "#15803d",
  OK: "#0a84ff",
  EINGESCHRAENKT: "#b45309",
};
const FLEX_BAND_LABEL: Record<FlexBand, string> = {
  GUT: "Gut",
  OK: "Okay",
  EINGESCHRAENKT: "Eingeschränkt",
};
const FLEX_REGION_ORDER: FlexRegion[] = [
  "hws",
  "schulter_brust",
  "rumpf",
  "huefte_hamstring",
  "knie_sprunggelenk",
];

export interface FlexTrendPoint {
  t: number;
  sessionId: string;
  flexAge: number;
}

function flexAgeRingSvg(value: number, size = 120): string {
  // Visually distinct from the clinical score ring (teal/blue gradient vs.
  // a single warm accent) so the two report types are never confused at a
  // glance.
  const stroke = 13;
  const r = (size - stroke) / 2;
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="none" stroke="#fde68a" stroke-width="${stroke}"/>
    <text x="50%" y="46%" text-anchor="middle" dominant-baseline="middle" font-size="${size * 0.28}" font-weight="800" fill="#0f172a">${value}</text>
    <text x="50%" y="68%" text-anchor="middle" dominant-baseline="middle" font-size="${size * 0.09}" fill="#92400e">Jahre</text>
  </svg>`;
}

function flexTrendChartSvg(points: FlexTrendPoint[]): string {
  const W = 760, H = 180, PL = 34, PR = 14, PT = 14, PB = 26;
  const ts = points.map((p) => p.t);
  const ages = points.map((p) => p.flexAge);
  const minT = Math.min(...ts), maxT = Math.max(...ts);
  const spanT = maxT - minT || 1;
  const lo = Math.min(...ages) - 2, hi = Math.max(...ages) + 2;
  const x = (t: number) => PL + ((t - minT) / spanT) * (W - PL - PR);
  const y = (v: number) => PT + (1 - (v - lo) / (hi - lo || 1)) * (H - PT - PB);
  let grid = "";
  const steps = [lo, (lo + hi) / 2, hi];
  for (const g of steps) {
    const yy = y(g);
    grid += `<line x1="${PL}" y1="${yy.toFixed(1)}" x2="${W - PR}" y2="${yy.toFixed(1)}" stroke="#e2e8f0" stroke-width="1"/>`;
    grid += `<text x="${PL - 6}" y="${(yy + 3).toFixed(1)}" text-anchor="end" font-size="9" fill="#94a3b8">${Math.round(g)}</text>`;
  }
  const sorted = [...points].sort((a, b) => a.t - b.t);
  const d = sorted.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.flexAge).toFixed(1)}`).join("");
  const line = sorted.length > 1 ? `<path d="${d}" fill="none" stroke="#d97706" stroke-width="2.5" stroke-linejoin="round"/>` : "";
  const dots = sorted.map((p) => `<circle cx="${x(p.t).toFixed(1)}" cy="${y(p.flexAge).toFixed(1)}" r="3.6" fill="#d97706"/>`).join("");
  const dl = (t: number) => new Date(t).toLocaleDateString();
  const xlabels =
    `<text x="${PL}" y="${H - 8}" font-size="9" fill="#64748b">${dl(minT)}</text>` +
    (maxT !== minT ? `<text x="${W - PR}" y="${H - 8}" text-anchor="end" font-size="9" fill="#64748b">${dl(maxT)}</text>` : "");
  return `<svg width="100%" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">${grid}${line}${dots}${xlabels}</svg>`;
}

function flexFindingRows(findings: FlexFinding[]): string {
  const byRegion = new Map<FlexRegion, FlexFinding[]>();
  for (const f of findings) {
    if (!byRegion.has(f.region)) byRegion.set(f.region, []);
    byRegion.get(f.region)!.push(f);
  }
  return FLEX_REGION_ORDER.filter((r) => (byRegion.get(r) ?? []).length > 0)
    .map((region) => {
      const rows = (byRegion.get(region) ?? [])
        .map(
          (f) =>
            `<tr><td>${esc(f.label)}${f.side ? ` (${f.side === "L" ? "links" : "rechts"})` : ""}</td><td style="color:${FLEX_BAND_COLOR[f.band]};font-weight:700">${FLEX_BAND_LABEL[f.band]}</td><td>${f.confidence}</td></tr>`,
        )
        .join("");
      return `<h2>${esc(FLEX_REGION_LABEL[region])}</h2><table><tbody>${rows}</tbody></table>`;
    })
    .join("");
}

export function buildFlexReportHtml(
  patient: Patient | null,
  result: FlexResult,
  history: FlexTrendPoint[] = [],
): string {
  const delta = result.flexAge.comparedToPrevious;
  const deltaPill =
    delta === null || delta === undefined
      ? ""
      : `<span style="font-weight:700;color:${delta < 0 ? "#15803d" : delta > 0 ? "#b45309" : "#64748b"}">${delta < 0 ? "▼ " : delta > 0 ? "▲ +" : "± "}${Math.abs(delta)} Jahr${Math.abs(delta) === 1 ? "" : "e"} vs. letzte Messung</span>`;

  return `<!doctype html><html lang="de"><head><meta charset="utf-8">
<title>CADtomie Flex — ${esc(patient?.code ?? "Screening")}</title>
<style>
  *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;margin:0}
  .wrap{max-width:820px;margin:0 auto;padding:0 28px 28px}
  .hdr{background:linear-gradient(120deg,#f59e0b,#d97706);color:#fff;padding:20px 28px;display:flex;align-items:center;gap:12px;margin:0 -28px 18px;border-radius:0 0 18px 18px}
  .hdr h1{font-size:20px;margin:0;font-weight:800;letter-spacing:-.02em}
  .hdr .sub{font-size:11px;opacity:.85}
  .hdr .right{margin-left:auto;text-align:right;font-size:11px;opacity:.9}
  h2{font-size:13px;text-transform:uppercase;letter-spacing:.05em;color:#b45309;margin:20px 0 8px}
  .demo{font-size:12px;color:#334155;line-height:1.7;display:flex;gap:24px;flex-wrap:wrap}
  .scorebox{display:flex;gap:22px;align-items:center}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0}
  .muted{color:#64748b;font-size:10px}
  .wellness{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:10px 14px;font-size:11.5px;color:#7c2d12;margin-top:18px;line-height:1.6}
  @media print{.hdr{-webkit-print-color-adjust:exact;print-color-adjust:exact}h2{page-break-after:avoid}}
</style></head><body>
  <div class="hdr">
    ${glyphSvg()}
    <div><h1>CADtomie Flex</h1><div class="sub">Flexibilitäts-Screening · Fitness/Wellness · on-device</div></div>
    <div class="right">Person <b>${esc(patient?.code ?? "—")}</b><br>${new Date(result.recordedAt).toLocaleDateString()}</div>
  </div>
  <div class="wrap">
    <div class="demo">
      ${result.flexAge.chronoAge != null ? `<span>Lebensalter ca.: <b>${result.flexAge.chronoAge}</b></span>` : ""}
      <span>Bewegungen erfasst: <b>${result.findings.length}</b></span>
    </div>

    <h2>Beweglichkeitsalter</h2>
    <div class="scorebox">${flexAgeRingSvg(result.flexAge.flexAge)}
      <div>${deltaPill ? `<div style="font-size:12.5px">${deltaPill}</div>` : ""}
      <p class="muted" style="margin-top:6px;max-width:420px">Spielerischer Fitness-Wert auf Basis interner Heuristiken — keine medizinische Kennzahl, kein Normwert.</p></div>
    </div>

    ${
      history.length > 1
        ? `<h2>Verlauf</h2>${flexTrendChartSvg(history)}`
        : ""
    }

    ${flexFindingRows(result.findings)}

    <div class="wellness">
      <b>CADtomie Flex ist ein spielerisches Fitness-/Wellness-Tool</b> — kein
      Medizinprodukt, keine klinische Diagnose, keine Therapieempfehlung und
      kein Ersatz für ärztliche oder physiotherapeutische Untersuchung. Die
      Ampel-Einstufungen und das Beweglichkeitsalter sind unterhaltsame
      Vergleichswerte auf Basis interner Heuristiken (Self-vs-Self), keine
      medizinischen Normwerte oder Gradangaben. Bei Beschwerden oder
      Unsicherheit bitte ärztlichen oder physiotherapeutischen Rat einholen.
      Versionen — Bewegungskatalog ${result.versions.movementCatalog}, Schwellen
      ${result.versions.threshold}, Altersmodell ${result.versions.ageModel}.
      Erstellt on-device — es haben keine Daten dieses Gerät verlassen.
    </div>
  </div>
</body></html>`;
}

export function openReportForPrint(html: string): void {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 450);
}

/**
 * Deliver a report to the user, adapting to the platform.
 *
 * On the web, `window.open()` + `window.print()` works fine — the browser
 * owns the print dialog and "Save as PDF". Inside a native Capacitor iOS/
 * Android build there is no browser chrome: `window.open("", "_blank")`
 * cannot spawn a real window and `window.print()` is a no-op, so the report
 * would silently fail to appear. Instead, write the HTML to the app cache
 * and hand it to the native Share Sheet — the user can open it in Safari
 * (which offers Print → Save to Files as PDF) or send the file directly via
 * Mail/Messages/AirDrop. `@capacitor/filesystem` and `@capacitor/share` are
 * already app dependencies; this only touches them on native platforms, so
 * web behavior is unchanged.
 */
export async function shareOrPrintReport(html: string, filenameBase = "cadtomie-report"): Promise<void> {
  let isNative = false;
  try {
    const { Capacitor } = await import("@capacitor/core");
    isNative = Capacitor.isNativePlatform();
  } catch {
    isNative = false;
  }

  if (!isNative) {
    openReportForPrint(html);
    return;
  }

  try {
    const { Filesystem, Directory, Encoding } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const fileName = `${filenameBase}-${Date.now()}.html`;
    const written = await Filesystem.writeFile({
      path: fileName,
      data: html,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    await Share.share({
      title: "CADtomie Report",
      url: written.uri,
      dialogTitle: "Report öffnen oder teilen",
    });
  } catch (err) {
    // Native share failed (e.g. simulator quirk, permissions) — fall back to
    // the print path rather than leaving the user with nothing.
    console.error("Native report share failed, falling back to print:", err);
    openReportForPrint(html);
  }
}
